<template>
  <div class="store-promocode">
    <div class="store-promocode__body">
      <div class="promo-toolbar">
        <hs-tabs :tabs="tabs" v-model="activeTab" />

        <div class="promo-toolbar__actions">
          <hs-button
            v-if="activeTab === 'codes' && checkedPromoCodes.length > 0"
            variant="secondary"
            icon="copy"
            @click="handleOpenCopyModal"
          >
            Copy to stores
          </hs-button>
          <hs-button
            v-if="activeTab === 'codes'"
            variant="primary"
            icon="plus"
            @click="handleOpenCreateModal"
          >
            Promo code
          </hs-button>

          <!-- Filters (show for usage tabs only) -->
          <div v-if="activeTab === 'store-promos' || activeTab === 'group-promos'"
            class="promo-toolbar__filters">
            <!-- Group Selector (Group Promos only) -->
            <div v-if="activeTab === 'group-promos'" class="filter-item">
              <label class="promo-toolbar__label">
                Select Group
              </label>
              <hs-select
                v-model="selectedGroupId"
                class="min-w-[180px]"
                :options="groupOptions"
                placeholder="Select Group"
                @update:modelValue="onGroupChange"
              />
            </div>

            <!-- Date Filters (Store & Group Promos) -->
            <hs-date-input
              v-model="dateFilters.startDate"
              label="From"
              placeholder="Start Date"
              :max="dateFilters.endDate"
            />
            <hs-date-input
              v-model="dateFilters.endDate"
              label="To"
              placeholder="End Date"
              :min="dateFilters.startDate"
            />
            <hs-button variant="secondary" icon="x-circle" @click="clearFilters">
              Clear
            </hs-button>
          </div>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="w-full mt-4">
        <!-- Codes Tab -->
        <div v-show="activeTab === 'codes'">
          <PromoCodes :promo-codes="allPromoCodes" @promo-codes-selected="handleCheckedItemsChanged"
            @promo-code-updated="handlePromoCodeUpdated" :is-loading="isLoadingPromoCodes" />
        </div>

        <!-- Store Promos Tab -->
        <div v-show="activeTab === 'store-promos'">
          <StorePromoUsageTable :store-id="selectedStore" :filters="dateFilters" />
        </div>

        <!-- Group Promos Tab -->
        <div v-show="activeTab === 'group-promos'">
          <GroupPromoUsageTable :store-id="selectedStore" :selected-group-id="selectedGroupId" :filters="dateFilters"
            @groups-loaded="handleGroupsLoaded" @group-changed="handleGroupChanged" />
        </div>
      </div>
    </div>

    <!-- Modals -->
    <div>
      <CreatePromoCode v-if="showCreateModal" @close-modal="handleModalClose" />
      <CopyPromoCodeToStore v-if="showCopyModal" :selectedPromoCodes="checkedPromoCodes"
        @close-modal="handleCopyModalClose" />
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import {
  usePromoCodes,
  useInvalidatePromoCodes,
} from "@/composables/usePromoCodesQuery";
import PromoCodes from "@/components/promo_code/PromoCodes.vue";
import StorePromoUsageTable from "@/components/promo_code/StorePromoUsageTable.vue";
import GroupPromoUsageTable from "@/components/promo_code/GroupPromoUsageTable.vue";
import CreatePromoCode from "@/components/modals/promo_code/CreatePromoCode";
import CopyPromoCodeToStore from "@/components/promo_code/CopyPromoCodeToStore.vue";
import { HsButton, HsDateInput, HsSelect, HsTabs } from "@/components/ui";

export default {
  name: "StorePromoCodesView",
  components: {
    PromoCodes,
    StorePromoUsageTable,
    GroupPromoUsageTable,
    CreatePromoCode,
    CopyPromoCodeToStore,
    HsButton,
    HsDateInput,
    HsSelect,
    HsTabs,
  },
  setup() {
    const store = useStore();
    const showCreateModal = ref(false);
    const showCopyModal = ref(false);
    const checkedPromoCodes = ref([]);
    const activeTab = ref("codes");
    const tabs = ref([
      { id: "codes", label: "Codes", icon: "tag" },
      { id: "store-promos", label: "Store Promos", icon: "shopping-bag" },
      { id: "group-promos", label: "Group Promos", icon: "users" },
    ]);

    // Filters state
    const dateFilters = ref({
      startDate: "",
      endDate: "",
    });
    const selectedGroupId = ref("");
    const availableGroups = ref([]);
    const groupOptions = computed(() => [
      { label: "Select Group", value: "" },
      ...availableGroups.value.map((group) => ({
        label: group.name,
        value: group.id,
      })),
    ]);

    const selectedStore = computed(() => store.state.dashboard?.selectedStore);

    // Use TanStack Query for promo codes
    const { data: promoCodesData, isLoading: isLoadingPromoCodes } =
      usePromoCodes(selectedStore);
    const allPromoCodes = computed(() => {
      const data = promoCodesData.value;
      if (!data) return [];
      // Ensure it's always an array
      return Array.isArray(data) ? data : [];
    });

    const { invalidatePromoCodes } = useInvalidatePromoCodes();

    const handleModalClose = () => {
      showCreateModal.value = false;
      invalidatePromoCodes(selectedStore.value);
    };

    const handleCopyModalClose = () => {
      showCopyModal.value = false;
    };

    const handleOpenCreateModal = () => {
      showCreateModal.value = true;
    };

    const handleOpenCopyModal = () => {
      showCopyModal.value = true;
    };

    const handleCheckedItemsChanged = (items) => {
      checkedPromoCodes.value = items;
    };

    const handlePromoCodeUpdated = () => {
      invalidatePromoCodes(selectedStore.value);
    };

    const clearFilters = () => {
      dateFilters.value.startDate = "";
      dateFilters.value.endDate = "";
    };

    const handleGroupsLoaded = (groups) => {
      availableGroups.value = groups;
    };

    const handleGroupChanged = (groupId) => {
      selectedGroupId.value = groupId;
    };

    const onGroupChange = () => {
      // Group change is handled by child component
    };

    return {
      showCreateModal,
      showCopyModal,
      checkedPromoCodes,
      activeTab,
      tabs,
      selectedStore,
      allPromoCodes,
      isLoadingPromoCodes,
      dateFilters,
      selectedGroupId,
      availableGroups,
      groupOptions,
      handleModalClose,
      handleCopyModalClose,
      handleOpenCreateModal,
      handleOpenCopyModal,
      handleCheckedItemsChanged,
      handlePromoCodeUpdated,
      clearFilters,
      handleGroupsLoaded,
      handleGroupChanged,
      onGroupChange,
    };
  },
};
</script>

<style scoped>
.store-promocode {
  background: transparent;
  min-height: 100vh;
  width: 100%;
}

.store-promocode__body {
  display: grid;
  gap: 1rem;
  padding: 0;
}

.promo-toolbar {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface) 92%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 14px;
  box-shadow: none;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 0.85rem;
}

.promo-toolbar__actions,
.promo-toolbar__filters {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;
}

.promo-toolbar__label {
  color: var(--hs-muted);
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 0.25rem;
}

@media (max-width: 900px) {
  .promo-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .promo-toolbar__actions,
  .promo-toolbar__filters {
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .store-promocode__body {
    padding: 0;
  }
}
</style>
